import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
    ArrowLeft,
    ArrowRight,
    CircleAlert,
    ListFilter,
    Search,
    Workflow,
    X,
} from 'lucide-react';
import { Fragment, useMemo } from 'react';
import { z } from 'zod';

import { OrganizationCombobox } from '@apps/web/components/admin/organization_combobox';
import { formatAutomationModeLabel } from '@apps/web/components/automations/admin_automation_helpers';
import {
    formatErrorMessage,
    useAdminAgentAutomationAttention,
    useAdminAgentAutomationRuns,
} from '@apps/web/components/automations/automation_api';
import {
    AUTOMATION_PAGE_SIZE,
    automationRunTriggerLabel,
} from '@apps/web/components/automations/automation_form';
import { RunStatusBadge } from '@apps/web/components/automations/badges';
import {
    type AdminAgentAutomationDeadLetterDispatchRead,
    type AdminAgentAutomationFailingScheduleRead,
    type AdminAgentAutomationRunRead,
} from '@apps/web/components/automations/types';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Input } from '@apps/web/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { formatDateAndTime } from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';
import { useAllAdminOrganizations } from '@apps/web/swr_data_fetching/admin_organization_queries';
import { type Mode } from '@libraries/typescript/api_client';

const PER_PAGE = AUTOMATION_PAGE_SIZE;
const RUN_STATUS_OPTIONS = [
    'idle',
    'running',
    'failed',
    'completed',
    'cancelled',
] as const;
type RunStatusOption = (typeof RUN_STATUS_OPTIONS)[number];
const FIRE_REASON_OPTIONS = ['scheduled', 'manual', 'event'] as const;
type FireReasonOption = (typeof FIRE_REASON_OPTIONS)[number];
const MODE_OPTIONS = ['live', 'sandbox'] as const;

const arrayParam = <T extends z.ZodTypeAny>(schema: T) =>
    z
        .preprocess((value) => {
            if (value === undefined || value === null) {
                return undefined;
            }
            if (Array.isArray(value)) {
                return value;
            }
            if (typeof value === 'string') {
                if (!value.trim()) {
                    return [];
                }
                return value
                    .split(',')
                    .map((item) => item.trim())
                    .filter(Boolean);
            }
            return value;
        }, z.array(schema))
        .optional();

const searchSchema = z.object({
    page: z.coerce.number().optional().default(1),
    organization_id: arrayParam(z.string()),
    automation_id: arrayParam(z.string()),
    automation_group_id: arrayParam(z.string()),
    run_status: arrayParam(z.enum(RUN_STATUS_OPTIONS)),
    fire_reason: arrayParam(z.enum(FIRE_REASON_OPTIONS)),
    mode: arrayParam(z.enum(MODE_OPTIONS)),
    needs_tool_approval: z.coerce.boolean().optional(),
    search: z.string().optional(),
});

type AdminAutomationRunsSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute('/_app/admin/agent/automations/runs')({
    head: () => ({
        meta: [
            {
                title: 'Automation Runs | Phoebe Admin',
            },
        ],
    }),
    validateSearch: searchSchema,
    component: AdminAutomationRunsPage,
});

function getSingleValue<T>(
    values: ReadonlyArray<T> | undefined,
): T | undefined {
    return values?.length === 1 ? values[0] : undefined;
}

function AdminAutomationRunsPage() {
    const navigate = Route.useNavigate();
    const search = Route.useSearch();
    const { isLoading: isLoadingOrganizations } = useAllAdminOrganizations({
        autoLoadPages: true,
        orgDataOnly: true,
    });

    const updateSearch = (patch: Partial<AdminAutomationRunsSearch>) => {
        void navigate({
            search: {
                ...search,
                ...patch,
                page: patch.page ?? 1,
            },
        });
    };

    const queryParams = useMemo(
        () => ({
            page: search.page,
            per_page: PER_PAGE,
            organization_id: search.organization_id,
            automation_id: search.automation_id,
            automation_group_id: search.automation_group_id,
            run_status: search.run_status as Array<RunStatusOption> | undefined,
            fire_reason: search.fire_reason as
                | Array<FireReasonOption>
                | undefined,
            mode: search.mode as Array<Mode> | undefined,
            needs_tool_approval: search.needs_tool_approval,
            search: search.search?.trim() || undefined,
        }),
        [
            search.automation_id,
            search.automation_group_id,
            search.fire_reason,
            search.mode,
            search.needs_tool_approval,
            search.organization_id,
            search.page,
            search.run_status,
            search.search,
        ],
    );
    const { data, error, hasRequestFailed, isLoading, isValidating } =
        useAdminAgentAutomationRuns(queryParams);
    const attention = useAdminAgentAutomationAttention();
    const items = data?.items ?? [];
    const triggerCounts = data?.trigger_counts ?? [];
    const total = data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
    const hasLoadError =
        error !== undefined || (hasRequestFailed && data === undefined);

    const hasFilters =
        (search.organization_id?.length ?? 0) > 0 ||
        (search.automation_id?.length ?? 0) > 0 ||
        (search.automation_group_id?.length ?? 0) > 0 ||
        (search.run_status?.length ?? 0) > 0 ||
        (search.fire_reason?.length ?? 0) > 0 ||
        (search.mode?.length ?? 0) > 0 ||
        search.needs_tool_approval !== undefined ||
        Boolean(search.search?.trim());
    // With an automation filter active every listed run shares one
    // automation, so any loaded row can label the filter chip.
    const automationFilterActive =
        (search.automation_id?.length ?? 0) > 0 ||
        (search.automation_group_id?.length ?? 0) > 0;
    const automationFilterLabel = automationFilterActive
        ? (items[0]?.automation_name ?? 'Automation')
        : null;

    return (
        <div className="flex h-full flex-col bg-[#F7FAFA] text-[#002429]">
            <div className="border-b border-[#DDE6E6] bg-white px-5 py-4">
                <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                        <h2 className="text-[17px] leading-6 font-semibold">
                            Automation Runs
                        </h2>
                        <p className="mt-0.5 text-sm text-[#5F7478]">
                            Recent agent automation runs across all
                            organizations.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="text-sm text-[#5F7478] tabular-nums">
                            {total} total
                        </div>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 border-[#DDE6E6] bg-white shadow-none"
                            asChild={true}
                        >
                            <Link to="/admin/agent/automations/list">
                                Automations
                            </Link>
                        </Button>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="relative min-w-0 basis-full sm:min-w-64 sm:flex-[1_1_18rem] xl:max-w-md">
                        <Search className="absolute top-2.5 left-3 h-4 w-4 text-[#829494]" />
                        <Input
                            value={search.search ?? ''}
                            onChange={(event) =>
                                updateSearch({
                                    search: event.target.value || undefined,
                                })
                            }
                            placeholder="Search automations or orgs…"
                            className="h-8 border-[#DDE6E6] bg-[#FBFDFD] pl-9 text-sm shadow-none"
                        />
                    </div>
                    <div className="min-w-0 basis-full sm:min-w-64 sm:flex-[1_1_18rem] xl:max-w-md">
                        <OrganizationCombobox
                            value={search.organization_id ?? []}
                            onValueChange={(value) =>
                                updateSearch({ organization_id: value })
                            }
                            placeholder="Filter organizations…"
                            disabled={isLoadingOrganizations}
                        />
                    </div>
                    <Select
                        value={getSingleValue(search.run_status) ?? 'all'}
                        onValueChange={(value) =>
                            updateSearch({
                                run_status:
                                    value === 'all'
                                        ? undefined
                                        : [value as RunStatusOption],
                            })
                        }
                    >
                        <SelectTrigger className="h-8 w-44 max-w-full border-[#DDE6E6] bg-[#FBFDFD] shadow-none">
                            <SelectValue placeholder="Run status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">
                                All run statuses
                            </SelectItem>
                            <SelectItem value="failed">Failed</SelectItem>
                            <SelectItem value="running">Running</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                            <SelectItem value="idle">Idle</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select
                        value={getSingleValue(search.fire_reason) ?? 'all'}
                        onValueChange={(value) =>
                            updateSearch({
                                fire_reason:
                                    value === 'all'
                                        ? undefined
                                        : [value as FireReasonOption],
                            })
                        }
                    >
                        <SelectTrigger className="h-8 w-44 max-w-full border-[#DDE6E6] bg-[#FBFDFD] shadow-none">
                            <SelectValue placeholder="Triggered by" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All triggers</SelectItem>
                            <SelectItem value="scheduled">Scheduled</SelectItem>
                            <SelectItem value="event">Event</SelectItem>
                            <SelectItem value="manual">Manual</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select
                        value={
                            search.needs_tool_approval === undefined
                                ? 'all'
                                : 'needs_approval'
                        }
                        onValueChange={(value) =>
                            updateSearch({
                                needs_tool_approval:
                                    value === 'needs_approval'
                                        ? true
                                        : undefined,
                            })
                        }
                    >
                        <SelectTrigger className="h-8 w-44 max-w-full border-[#DDE6E6] bg-[#FBFDFD] shadow-none">
                            <SelectValue placeholder="Approval" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All runs</SelectItem>
                            <SelectItem value="needs_approval">
                                Needs approval
                            </SelectItem>
                        </SelectContent>
                    </Select>
                    <Select
                        value={getSingleValue(search.mode) ?? 'all'}
                        onValueChange={(value) =>
                            updateSearch({
                                mode:
                                    value === 'all'
                                        ? undefined
                                        : [value as Mode],
                            })
                        }
                    >
                        <SelectTrigger className="h-8 w-36 max-w-full border-[#DDE6E6] bg-[#FBFDFD] shadow-none">
                            <SelectValue placeholder="Mode" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All modes</SelectItem>
                            <SelectItem value="live">Live</SelectItem>
                            <SelectItem value="sandbox">Sandbox</SelectItem>
                        </SelectContent>
                    </Select>
                    {automationFilterLabel !== null ? (
                        <Badge
                            variant="secondary"
                            className="h-8 max-w-64 gap-1 px-2"
                        >
                            <span className="truncate">
                                Automation: {automationFilterLabel}
                            </span>
                            <button
                                type="button"
                                aria-label="Clear automation filter"
                                onClick={() =>
                                    updateSearch({
                                        automation_id: undefined,
                                        automation_group_id: undefined,
                                    })
                                }
                                className="shrink-0"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </Badge>
                    ) : null}
                    <div className="flex h-9 max-w-full shrink-0 items-center gap-2 whitespace-nowrap">
                        {hasFilters ? (
                            <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                aria-label="Clear filters"
                                onClick={() =>
                                    void navigate({
                                        search: { page: 1 },
                                    })
                                }
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        ) : null}
                    </div>
                </div>
            </div>
            <div className="flex-1 space-y-4 overflow-auto p-5">
                <AttentionSection
                    deadLetterDispatches={
                        attention.data?.dead_letter_dispatches ?? []
                    }
                    failingSchedules={attention.data?.failing_schedules ?? []}
                />
                {triggerCounts.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-medium tracking-wide text-[#5F7478] uppercase">
                            Trigger causes
                        </span>
                        {triggerCounts.map((triggerCount) => (
                            <Badge
                                key={triggerCount.trigger}
                                variant="outline"
                                className="border-[#DDE6E6] bg-white text-[#253F44]"
                            >
                                {triggerCount.label} · {triggerCount.count}
                            </Badge>
                        ))}
                    </div>
                ) : null}
                <div className="overflow-hidden rounded-[8px] border border-[#DDE6E6] bg-white shadow-[0_1px_2px_rgba(0,36,41,0.04)]">
                    <Table
                        containerClassName="overflow-x-auto"
                        className="min-w-[1060px] table-fixed"
                    >
                        <colgroup>
                            <col className="w-[12%]" />
                            <col className="w-[19%]" />
                            <col className="w-[15%]" />
                            <col className="w-[7%]" />
                            <col className="w-[10%]" />
                            <col className="w-[10%]" />
                            <col className="w-[27%]" />
                        </colgroup>
                        <TableHeader className="bg-[#FBFDFD]">
                            <TableRow className="border-[#DDE6E6] hover:bg-transparent">
                                <TableHead className="h-9 px-3 text-[11px] font-semibold text-[#5F7478] uppercase">
                                    Started
                                </TableHead>
                                <TableHead className="h-9 px-3 text-[11px] font-semibold text-[#5F7478] uppercase">
                                    Automation
                                </TableHead>
                                <TableHead className="h-9 px-3 text-[11px] font-semibold text-[#5F7478] uppercase">
                                    Organization
                                </TableHead>
                                <TableHead className="h-9 px-3 text-[11px] font-semibold text-[#5F7478] uppercase">
                                    Mode
                                </TableHead>
                                <TableHead className="h-9 px-3 text-[11px] font-semibold text-[#5F7478] uppercase">
                                    Status
                                </TableHead>
                                <TableHead className="h-9 px-3 text-[11px] font-semibold text-[#5F7478] uppercase">
                                    Triggered by
                                </TableHead>
                                <TableHead className="h-9 px-3 text-[11px] font-semibold text-[#5F7478] uppercase">
                                    Notes
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow className="hover:bg-transparent">
                                    <TableCell
                                        colSpan={7}
                                        className="h-28 text-center"
                                    >
                                        <Spinner
                                            size="lg"
                                            className="mx-auto text-gray-500"
                                        />
                                    </TableCell>
                                </TableRow>
                            ) : null}
                            {!isLoading && hasLoadError ? (
                                <TableRow className="hover:bg-transparent">
                                    <TableCell
                                        colSpan={7}
                                        className="h-28 text-center text-sm text-red-600"
                                    >
                                        {formatErrorMessage(
                                            error,
                                            'Failed to load automation runs.',
                                        )}
                                    </TableCell>
                                </TableRow>
                            ) : null}
                            {!isLoading &&
                            !hasLoadError &&
                            items.length === 0 ? (
                                <TableRow className="hover:bg-transparent">
                                    <TableCell
                                        colSpan={7}
                                        className="h-32 text-center"
                                    >
                                        <div className="space-y-2">
                                            <Workflow className="mx-auto h-8 w-8 text-gray-500" />
                                            <div className="font-medium">
                                                No runs found
                                            </div>
                                            <p className="text-muted-foreground text-sm">
                                                Adjust the filters to see more
                                                runs.
                                            </p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : null}
                            {items.map((run) => (
                                <AutomationRunRow
                                    key={run.id}
                                    run={run}
                                    onFilterToAutomation={() =>
                                        // Filter by group so runs from every
                                        // target org of a multi-org
                                        // automation stay visible.
                                        updateSearch({
                                            automation_id: undefined,
                                            automation_group_id: [
                                                run.automation_group_id,
                                            ],
                                        })
                                    }
                                />
                            ))}
                        </TableBody>
                    </Table>
                </div>
                <div className="flex items-center justify-between">
                    <div className="text-sm text-[#5F7478]">
                        Page {search.page} of {totalPages}
                        {isValidating && !isLoading ? ' · Refreshing' : ''}
                    </div>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 border-[#DDE6E6] bg-white shadow-none"
                            disabled={search.page <= 1}
                            onClick={() =>
                                updateSearch({ page: search.page - 1 })
                            }
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Previous
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 border-[#DDE6E6] bg-white shadow-none"
                            disabled={search.page >= totalPages}
                            onClick={() =>
                                updateSearch({ page: search.page + 1 })
                            }
                        >
                            Next
                            <ArrowRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Failures that never produce a run row: dead-lettered event dispatches and
// schedule triggers with a current error (repeated errors auto-pause the
// automation without any run appearing in the table below).
function AttentionSection({
    deadLetterDispatches,
    failingSchedules,
}: {
    readonly deadLetterDispatches: ReadonlyArray<AdminAgentAutomationDeadLetterDispatchRead>;
    readonly failingSchedules: ReadonlyArray<AdminAgentAutomationFailingScheduleRead>;
}) {
    if (deadLetterDispatches.length === 0 && failingSchedules.length === 0) {
        return null;
    }
    return (
        <div className="space-y-3 rounded-[8px] border border-amber-300 bg-amber-50 p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                <CircleAlert className="h-4 w-4" />
                Needs attention
            </div>
            {failingSchedules.map((schedule) => (
                <div
                    key={schedule.trigger_id}
                    className="text-sm text-amber-900"
                >
                    <Link
                        to="/admin/agent/automations/$organizationId/$automationId"
                        params={{
                            organizationId: schedule.organization_id,
                            automationId: schedule.automation_id,
                        }}
                        search={{ mode: schedule.mode }}
                        className="font-medium hover:underline"
                    >
                        {schedule.automation_name}
                    </Link>{' '}
                    (
                    {schedule.organization_internal_name?.trim() ||
                        schedule.organization_name}
                    ) — schedule failing ({schedule.failure_count}{' '}
                    {schedule.failure_count === 1 ? 'failure' : 'failures'}
                    {schedule.automation_status === 'paused'
                        ? ', auto-paused'
                        : ''}
                    ): <span className="font-mono">{schedule.last_error}</span>
                </div>
            ))}
            {deadLetterDispatches.map((dispatch) => (
                <div key={dispatch.id} className="text-sm text-amber-900">
                    <Link
                        to="/admin/agent/automations/$organizationId/$automationId"
                        params={{
                            organizationId: dispatch.organization_id,
                            automationId: dispatch.automation_id,
                        }}
                        search={{ mode: dispatch.mode }}
                        className="font-medium hover:underline"
                    >
                        {dispatch.automation_name}
                    </Link>{' '}
                    (
                    {dispatch.organization_internal_name?.trim() ||
                        dispatch.organization_name}
                    ) — event dispatch dead-lettered after{' '}
                    {dispatch.attempt_count} attempts
                    {dispatch.last_error ? (
                        <Fragment>
                            {': '}
                            <span className="font-mono">
                                {dispatch.last_error}
                            </span>
                        </Fragment>
                    ) : null}
                </div>
            ))}
        </div>
    );
}

function AutomationRunRow({
    run,
    onFilterToAutomation,
}: {
    readonly run: AdminAgentAutomationRunRead;
    readonly onFilterToAutomation: () => void;
}) {
    const navigate = useNavigate();
    const displayOrganizationName =
        run.organization_internal_name?.trim() || run.organization_name;
    // The whole row opens the run detail; skipped launches have no agent
    // run to open, so those rows are inert. The automation name stays a
    // separate link to the automation itself.
    const canOpenRun = run.phoebe_agent_run_id !== null;
    return (
        <TableRow
            className={cn(
                'h-14 border-[#E6EEEE] hover:bg-[#F7FAFA]',
                canOpenRun && 'cursor-pointer',
            )}
            onClick={
                canOpenRun
                    ? () =>
                          void navigate({
                              to: '/admin/agent/automations/$organizationId/$automationId/runs/$automationRunId',
                              params: {
                                  organizationId: run.organization_id,
                                  automationId: run.automation_id,
                                  automationRunId: run.id,
                              },
                              search: { mode: run.mode },
                          })
                    : undefined
            }
        >
            <TableCell className="truncate px-3 py-2 text-[13px] text-[#253F44]">
                {formatDateAndTime(run.started_at, run.organization_timezone)}
            </TableCell>
            <TableCell className="px-3 py-2">
                <div className="group/automation flex min-w-0 items-center gap-1">
                    <Link
                        to="/admin/agent/automations/$organizationId/$automationId"
                        params={{
                            organizationId: run.organization_id,
                            automationId: run.automation_id,
                        }}
                        search={{ mode: run.mode }}
                        onClick={(event) => event.stopPropagation()}
                        className="block truncate text-[13px] font-semibold text-[#002429] hover:underline"
                    >
                        {run.automation_name}
                    </Link>
                    <button
                        type="button"
                        aria-label={`Filter runs to ${run.automation_name}`}
                        title="Filter runs to this automation"
                        onClick={(event) => {
                            event.stopPropagation();
                            onFilterToAutomation();
                        }}
                        className="shrink-0 rounded p-0.5 text-[#829494] opacity-0 group-hover/automation:opacity-100 hover:bg-[#EEF4F4] hover:text-[#002429] focus-visible:opacity-100"
                    >
                        <ListFilter className="h-3.5 w-3.5" />
                    </button>
                </div>
            </TableCell>
            <TableCell className="truncate px-3 py-2 text-[13px] text-[#253F44]">
                {displayOrganizationName}
            </TableCell>
            <TableCell className="px-3 py-2">
                <Badge
                    variant="outline"
                    className="border-[#DDE6E6] text-[#253F44]"
                >
                    {formatAutomationModeLabel(run.mode)}
                </Badge>
            </TableCell>
            <TableCell className="px-3 py-2">
                <RunStatusBadge run={run} />
            </TableCell>
            <TableCell className="truncate px-3 py-2 text-[13px] text-[#253F44]">
                {automationRunTriggerLabel(run)}
                {run.is_dry_run ? ' · dry run' : ''}
            </TableCell>
            <TableCell className="px-3 py-2">
                <div
                    className="truncate text-[13px] text-[#5F7478]"
                    title={run.skipped_reason ?? undefined}
                >
                    {run.skipped_reason ?? ''}
                </div>
            </TableCell>
        </TableRow>
    );
}
